import React from 'react';

import Icon from '../ui/Icon';
import { ICONS } from '../ui/icon-map';
import DataTable from '../ui/DataTable/DataTable';
import { CellText, CellStatus, CellTag } from '../ui/DataTable/cells';
import EmptyState from '../ui/EmptyState';
import Button from '../ui/Button';

// Invygo / Yelo remarks read as a category tag (Invygo purple, Yelo amber); other remarks are plain text.
const remarksCell = (text) => {
    const str = String(text || '');
    const lower = str.toLowerCase();
    if (lower.includes('invygo')) return <CellTag tone="accent">{str}</CellTag>;
    if (lower.includes('yelo')) return <CellTag tone="warning">{str}</CellTag>;
    return <CellText>{str}</CellText>;
};

// Car status from the rental system: "ready" is a good state the shared map doesn't know;
// "rented" stays blue as before.
const CAR_STATUS_TONES = { ready: 'success', rented: 'info' };
const CarStatusCell = ({ status }) => {
    if (!status) return <span className="ds-cell-empty">—</span>;
    return <CellStatus status={status} tone={CAR_STATUS_TONES[String(status).toLowerCase()]} />;
};

// One tile per class / model: name and car count centred in the tile.
const NavTile = ({ label, count, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        className="card card--interactive h-[110px] p-4 flex flex-col items-center justify-center gap-1 text-center"
    >
        <span className="font-semibold text-base text-gray-900 w-full break-words leading-tight">{label}</span>
        <span className="text-xs text-gray-500 tabular-nums">{count} {count === 1 ? 'car' : 'cars'}</span>
    </button>
);

// "Seltos" / "SELTOS " / "seltos" are one model: group by a normalised key and show the most
// common spelling.
const norm = (v) => String(v ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
const groupBy = (rows, pick) => {
    const groups = new Map();
    rows.forEach((row) => {
        const raw = pick(row);
        const key = norm(raw);
        if (!key) return;
        const g = groups.get(key) || { key, count: 0, spellings: new Map() };
        g.count += 1;
        const label = String(raw).trim().replace(/\s+/g, ' ');
        g.spellings.set(label, (g.spellings.get(label) || 0) + 1);
        groups.set(key, g);
    });
    return [...groups.values()]
        .map((g) => ({ key: g.key, count: g.count, label: [...g.spellings.entries()].sort((a, b) => b[1] - a[1])[0][0] }))
        .sort((a, b) => a.label.localeCompare(b.label));
};
const classOf = (row) => row.category || row.Class;
const modelOf = (row) => row.model || row.Model;

const SmartNavView = ({
    data,
    selectedClass,
    setSelectedClass,
    selectedMake,
    setSelectedMake,
    getCarStatus
}) => {

    // Classes, then the models of the selected class (selection is stored as the display label)
    const uniqueClasses = groupBy(data, classOf);
    const inClass = selectedClass ? data.filter((r) => norm(classOf(r)) === norm(selectedClass)) : [];
    const uniqueModels = selectedClass ? groupBy(inClass, modelOf) : [];

    const filteredData = inClass.filter((car) => norm(modelOf(car)) === norm(selectedMake));

    const columns = [
        { key: 'category', header: 'Class', type: 'text' },
        { key: 'make', header: 'Manufacturer', type: 'text' },
        { key: 'model', header: 'Model', type: 'text' },
        { key: 'plate_number', header: 'Plate No', type: 'plate' },
        { key: 'color', header: 'Color', type: 'text' },
        { key: 'registration_expiry', header: 'Reg Expiry', nowrap: true, type: 'date' },
        { key: 'insurance_expiry', header: 'Insur Expiry', nowrap: true, type: 'date' },
        { key: 'remarks', header: 'Remarks', cell: (row) => remarksCell(row.remarks) },
        { key: 'status', header: 'Status', type: 'status' },
        {
            key: 'car_status',
            header: 'Car Status',
            accessor: (row) => (getCarStatus ? getCarStatus(row.plate_number) : ''),
            cell: (row, value) => <CarStatusCell status={value} />,
        },
    ];

    return (
        <div className="w-full mt-6">
            {/* 1. Classes Grid */}
            {!selectedClass && (
                uniqueClasses.length === 0 ? (
                    <EmptyState icon={ICONS.car} title="No classes to show" />
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {uniqueClasses.map((cls) => (
                            <NavTile key={cls.key} label={cls.label} count={cls.count} onClick={() => setSelectedClass(cls.label)} />
                        ))}
                    </div>
                )
            )}

            {/* 2. Models Grid (inside Class) */}
            {selectedClass && !selectedMake && (
                <div className="flex flex-col gap-4">
                    <div className="ds-toolbar">
                        <Button
                            onClick={() => setSelectedClass(null)}
                            size="sm"
                            leadingIcon={<Icon glyph={ICONS.back} size="sm" />}
                        >
                            Back to Classes
                        </Button>
                        <h2 className="text-base font-semibold text-gray-900 m-0">{selectedClass} Models</h2>
                    </div>
                    {uniqueModels.length === 0 ? (
                        <EmptyState icon={ICONS.car} title="No models in this class" />
                    ) : (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {uniqueModels.map((model) => (
                                <NavTile key={model.key} label={model.label} count={model.count} onClick={() => setSelectedMake(model.label)} />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* 3. Final Table View */}
            {selectedClass && selectedMake && (
                <div className="flex flex-col gap-3">
                    <div className="ds-toolbar">
                        <Button
                            onClick={() => setSelectedMake(null)}
                            size="sm"
                            leadingIcon={<Icon glyph={ICONS.back} size="sm" />}
                        >
                            Back to Models
                        </Button>
                    </div>
                    <DataTable
                        title={<span>{selectedMake} <span className="text-gray-500 font-normal">({filteredData.length})</span></span>}
                        columns={columns}
                        data={filteredData}
                        rowKey={(row, i) => `${row.plate_number}-${i}`}
                        searchPlaceholder="Search plate, colour, remarks…"
                        emptyTitle="No cars for this model"
                    />
                </div>
            )}
        </div>
    );
};

export default SmartNavView;
